import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

export default function LoginPage() {
  const { login, loginWithGoogle, user } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate("/dashboard");
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center pt-16 bg-[#F9FAFB] px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="bg-white border border-border p-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold tracking-tight" data-testid="login-title">{t("auth.login_title")}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t("auth.login_subtitle")}</p>
          </div>

          <Button
            variant="outline"
            className="w-full mb-6 rounded-sm border-border hover:bg-secondary"
            onClick={() => loginWithGoogle("/dashboard")}
            data-testid="google-login-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            {t("auth.google_btn")}
          </Button>

          <div className="flex items-center gap-3 mb-6">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground uppercase">{t("auth.or")}</span>
            <Separator className="flex-1" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 p-3" data-testid="login-error">{error}</div>}
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider">{t("auth.email")}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 rounded-sm" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider">{t("auth.password")}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 rounded-sm" data-testid="login-password-input" />
            </div>
            <Button type="submit" className="w-full bg-[#0A3FA3] hover:bg-[#082F7A] rounded-sm" disabled={loading} data-testid="login-submit-btn">
              {loading ? "..." : t("auth.login_btn")}
            </Button>
          </form>

          <p className="text-sm text-center text-muted-foreground mt-6">
            {t("auth.no_account")}{" "}
            <Link to="/register" className="text-[#0A3FA3] font-medium hover:underline" data-testid="login-register-link">{t("nav.register")}</Link>
          </p>
          <p className="text-sm text-center mt-2">
            <Link to="/forgot-password" className="text-muted-foreground hover:text-[#0A3FA3] hover:underline text-xs" data-testid="forgot-password-link">
              {lang === "nl" ? "Wachtwoord vergeten?" : lang === "de" ? "Passwort vergessen?" : lang === "fr" ? "Mot de passe oubli\u00e9?" : "Forgot password?"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
